import { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import type { DateRange } from "react-day-picker";
import MerchantContext from "@/contexts/MerchantContext";
import UserContext from "@/contexts/UserContext";
import { usePipelinePositions, type PipelinePosition } from "@/contexts/PipelineContext";
import { isDataScopedEmployee } from "@/utils/permissions";
import { hasTickets } from "@/utils/pipelines";
import { Spinner } from "@/components/ui/spinner";
import posthog from "@/lib/posthog";
import { trpc } from "@/trpc";
import { mapSource } from "@/utils/valueMap";
import SubHeaderFiltros from "@/components/tablero/SubHeaderFiltros";
import KanbanBoard, { PhoneColumns } from "@/components/tablero/KanbanBoard";
import ListaView, { ListaRows } from "@/components/tablero/ListaView";
import NonRealtimeSearchBoard from "@/components/tablero/NonRealtimeSearchBoard";
import { InactivoSelectionProvider } from "@/components/tablero/InactivoSelectionContext";
import FunnelComercial from "@/components/tablero/inactivos/FunnelComercial";
import { InactivoBroadcastBridge } from "@/components/tablero/inactivos/FloatingActionBar";
import ProspectoDrawer from "@/components/tablero/drawer/ProspectoDrawer";
import SolicitudesBoard from "@/components/tablero/solicitudes/SolicitudesBoard";
import { PIPELINE_AGENT_TOPIC } from "@/components/activities/serviceRequests/constants";
import TicketDrawer from "@/components/activities/serviceRequests/TicketDrawer";
import {
  EMPTY_SOLICITUD_FILTROS,
  isSolicitudFilterMode,
  type SolicitudFiltros,
  type TicketPosition,
} from "@/components/tablero/solicitudes/types";
import {
  ANY_PAUTA,
  STAGE_ORDER,
  pautaQueryFilters,
  sourceLabel,
  type Stage,
  type TableroCardLead,
  type TableroQueryFilters,
} from "@/components/tablero/types";

export type TableroFiltros = {
  // Multi-select (Tinybird handles IN); the real-time index path takes a single
  // employeeId, so selecting more than one forces non-realtime mode.
  asesores: string[];
  etapa: string | null;
  fuente: string | null;
  pauta: string | null;
  tagIds: string[];
  query: string;
  soloAtencionHumana: boolean;
};

export const EMPTY_FILTROS: TableroFiltros = {
  asesores: [],
  etapa: null,
  fuente: null,
  pauta: null,
  tagIds: [],
  query: "",
  soloAtencionHumana: false,
};

type Tab = "activos" | "inactivos";
type Vista = "kanban" | "lista";
// Ticket pipelines (mantenimiento / administrativo) swap the whole board
// between their leads and their solicitudes; Comercial has no such split.
type Modo = "leads" | "solicitudes";

// Switcher order: reserved pipelines first, then customs alphabetically.
// "collections" (Cartera) is legacy and never offered.
const RESERVED_PIPELINE_ORDER = ["default", "sourceproperties", "maintenance", "general"];

// Inactivos defaults to the last 30 days (by creation date) so opening the tab
// doesn't fetch the merchant's entire inactive history; the user can widen or
// remove the range from the date filter.
const defaultDateRange = (tab: Tab): DateRange | undefined =>
  tab === "inactivos"
    ? { from: dayjs().subtract(30, "day").toDate(), to: dayjs().toDate() }
    : undefined;

// Solicitudes always carries a date range: pipelineId is a filter expression
// on the ticket store, not an index key, so an unbounded query scans the whole
// status partition. The month default keeps it cheap; the user can widen it.
const currentMonthRange = (): DateRange => ({
  from: dayjs().startOf("month").toDate(),
  to: dayjs().endOf("month").toDate(),
});

const TableroContainer = () => {
  const { merchantId, broadcastEnabled, merchantType } =
    useContext(MerchantContext);
  const {
    roles,
    employeeIds,
    pipelinePermissions,
    isLoading: isLoadingUser,
  } = useContext(UserContext);
  const isDataScoped = isDataScopedEmployee(roles);
  // Read-once URL ingestion: pipeline, search and open lead come from the
  // query string on mount; the write effect below keeps the URL mirroring
  // them from then on, so nothing re-reads searchParams after this.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") ?? "";
  const [pipelineId, setPipelineId] = useState(() => searchParams.get("pipeline") ?? "default");
  const isDefaultPipeline = pipelineId === "default";
  const [tab, setTab] = useState<Tab>("activos");
  // Data-scoped employees have no Inactivos tab (the toggle is hidden); the
  // pin makes the invariant hold even against races or future deep links.
  const effectiveTab: Tab = isDataScoped ? "activos" : tab;
  const [vista, setVista] = useState<Vista>("kanban");
  // Only meaningful on the first render (it seeds the two useState calls
  // below). A ?modo=solicitudes link is honored only where the toggle itself
  // would be offered, so an ineligible link lands on Leads with Leads'
  // defaults rather than a half-applied solicitudes state.
  const initialModo: Modo =
    searchParams.get("modo") === "solicitudes" && hasTickets(pipelineId) && !isDataScoped
      ? "solicitudes"
      : "leads";
  const [modo, setModo] = useState<Modo>(initialModo);
  // Solicitudes only exists on ticket pipelines, and never for data-scoped
  // employees: the serviceRequests procedures have no employee scoping, so a
  // scoped user must not reach that board. Pinning here (rather than trusting
  // the toggle to stay hidden) holds the invariant against a pipeline switch
  // racing the state reset.
  const effectiveModo: Modo = hasTickets(pipelineId) && !isDataScoped ? modo : "leads";
  const [filtros, setFiltros] = useState<TableroFiltros>({
    ...EMPTY_FILTROS,
    query: initialQuery,
  });
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() =>
    initialModo === "solicitudes" ? currentMonthRange() : undefined
  );
  const [selectedLeadId, setSelectedLeadId] = useState<string | undefined>(
    () => searchParams.get("lead") ?? undefined
  );
  const [selectedSolicitudId, setSelectedSolicitudId] = useState<string | undefined>(
    () => searchParams.get("solicitud") ?? undefined
  );
  const [solicitudFiltros, setSolicitudFiltros] =
    useState<SolicitudFiltros>(EMPTY_SOLICITUD_FILTROS);
  // Solicitudes has its own search box (name / phone / cédula), separate from
  // the lead `filtros.query`. It always runs against the non-realtime ticket
  // search endpoint, so there's no realtime/phone-mode branching to gate it.
  const [solicitudQuery, setSolicitudQuery] = useState("");
  // Every visit starts in real-time mode; the manual opt-out is in-session,
  // not a persisted preference.
  const [manualRealtimeOff, setManualRealtimeOff] = useState(false);

  // The query is debounced before it reaches any query input or the mode
  // derivation, so typing doesn't refire searches or flip the board per
  // keystroke. Handlers that clear the query also snap this so the mode
  // doesn't lag the clear by a debounce tick.
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(filtros.query), 300);
    return () => clearTimeout(t);
  }, [filtros.query]);

  // Same 300ms debounce for the Solicitudes search box, so typing doesn't
  // refire the ticket-search query per keystroke.
  const [debouncedSolicitudQuery, setDebouncedSolicitudQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSolicitudQuery(solicitudQuery), 300);
    return () => clearTimeout(t);
  }, [solicitudQuery]);

  // Data-scoped employees are locked to their linked asesores; the asesor
  // filter can only narrow within them, and an empty selection means all of
  // them. Derived at the point of use so filter resets don't need to know
  // about scoping.
  const effectiveAsesores = useMemo(() => {
    if (!isDataScoped) return filtros.asesores;
    const chosen = filtros.asesores.filter((id) => employeeIds.includes(id));
    return chosen.length ? chosen : employeeIds;
  }, [isDataScoped, employeeIds, filtros.asesores]);
  const showAsesorFilter = !isDataScoped || employeeIds.length > 1;

  // The mode is derived from the filters: anything only the non-realtime store
  // can serve (fuente, pauta, atención humana, multi-asesor) turns real-time off
  // automatically, and clearing those filters turns it back on. The switch is an
  // indicator plus a manual opt-out.
  //
  // A search no longer belongs in this list. It used to, because an alphabetic
  // query could only be answered by Tinybird's name scan; now the phone and both
  // WhatsApp handles are answered from DynamoDB in real time and only the name
  // half comes from Tinybird, labelled per row. Search is its own mode below and
  // overrides this one.
  const realtimeLocked = isDataScoped && effectiveAsesores.length > 1;
  const requiresNonRealtime =
    !!filtros.fuente ||
    !!filtros.pauta ||
    filtros.tagIds.length > 0 ||
    filtros.soloAtencionHumana ||
    effectiveAsesores.length > 1;
  const realtime = !manualRealtimeOff && !requiresNonRealtime;

  const handleRealtimeChange = (v: boolean) => {
    if (realtimeLocked) return;
    setManualRealtimeOff(!v);
    // Forcing real-time ON clears the filters only the non-realtime store can
    // serve — the derived mode can't turn on while they're set. A multi-asesor
    // selection is cleared rather than silently narrowed to an arbitrary one.
    // The query survives: it is served by both stores now.
    if (v) {
      setFiltros({
        ...filtros,
        fuente: null,
        pauta: null,
        tagIds: [],
        soloAtencionHumana: false,
        asesores: filtros.asesores.length > 1 ? [] : filtros.asesores,
      });
    }
  };

  useEffect(() => {
    if (!merchantId) return;
    posthog.capture("tablero viewed", { merchant_id: merchantId, pipeline_id: pipelineId });
  }, [merchantId, pipelineId]);

  // The URL always mirrors the shareable subset of state (pipeline, search,
  // open lead); defaults are omitted so the bare /tablero URL stays clean.
  // replace: true — filter/drawer churn shouldn't pollute browser history.
  useEffect(() => {
    const params: Record<string, string> = {};
    if (pipelineId !== "default") params.pipeline = pipelineId;
    if (effectiveModo !== "leads") params.modo = effectiveModo;
    if (filtros.query) params.q = filtros.query;
    if (selectedLeadId) params.lead = selectedLeadId;
    if (selectedSolicitudId) params.solicitud = selectedSolicitudId;
    setSearchParams(params, { replace: true });
  }, [
    pipelineId,
    effectiveModo,
    filtros.query,
    selectedLeadId,
    selectedSolicitudId,
    setSearchParams,
  ]);

  // Tab switch is a context change: clear filters and close the drawer. A
  // handler (not a [tab] effect) so mount doesn't wipe URL-initialized state.
  const handleTabChange = (t: Tab) => {
    setTab(t);
    setFiltros(EMPTY_FILTROS);
    setDebouncedQuery("");
    setDateRange(defaultDateRange(t));
    setSelectedLeadId(undefined);
  };

  // Mode switch is a context change, same as a tab change: clear filters and
  // close both drawers. Solicitudes carries its own date default (see
  // currentMonthRange).
  const handleModoChange = (m: Modo) => {
    setModo(m);
    setFiltros(EMPTY_FILTROS);
    setSolicitudFiltros(EMPTY_SOLICITUD_FILTROS);
    setDebouncedQuery("");
    setSolicitudQuery("");
    setDebouncedSolicitudQuery("");
    setDateRange(m === "solicitudes" ? currentMonthRange() : defaultDateRange("activos"));
    setSelectedLeadId(undefined);
    setSelectedSolicitudId(undefined);
  };

  const { data: pipelinesData } = trpc.pipeline.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId, staleTime: 5 * 60 * 1000 }
  );
  const pipelines = useMemo(() => {
    const list = (pipelinesData ?? [])
      .filter((p) => p.id !== "collections")
      .map((p) => ({ id: p.id, label: p.name }));
    const rank = (id: string) => {
      const index = RESERVED_PIPELINE_ORDER.indexOf(id);
      return index === -1 ? RESERVED_PIPELINE_ORDER.length : index;
    };
    return [...list].sort((a, b) => rank(a.id) - rank(b.id) || a.label.localeCompare(b.label));
  }, [pipelinesData]);

  // Data-scoped employees only see pipelines they have permission for (same
  // model as the inbox); null means no filtering applies.
  const allowedPipelineIdSet = useMemo(() => {
    if (!isDataScoped) return null;
    return new Set(pipelinePermissions.map((p) => p.id));
  }, [isDataScoped, pipelinePermissions]);
  const visiblePipelines = useMemo(
    () =>
      allowedPipelineIdSet
        ? pipelines.filter((p) => allowedPipelineIdSet.has(p.id))
        : pipelines,
    [pipelines, allowedPipelineIdSet]
  );

  // Switching pipeline is a full context change: same reset as a tab change,
  // plus non-default pipelines have no Inactivos tab (inactive is a board
  // column there), so the tab snaps back to Activos.
  const handlePipelineChange = (id: string) => {
    setPipelineId(id);
    setTab("activos");
    setModo("leads");
    setFiltros(EMPTY_FILTROS);
    setSolicitudFiltros(EMPTY_SOLICITUD_FILTROS);
    setDebouncedQuery("");
    setSolicitudQuery("");
    setDebouncedSolicitudQuery("");
    setDateRange(defaultDateRange("activos"));
    setSelectedLeadId(undefined);
    setSelectedSolicitudId(undefined);
  };

  // A shared link may name a pipeline this merchant doesn't have (stale or
  // cross-merchant); snap back to default once the real list is known. For
  // data-scoped users the current pipeline (including "default") must also be
  // permitted — snap to the first permitted one, never blindly to "default".
  useEffect(() => {
    if (!pipelinesData) return;
    if (allowedPipelineIdSet) {
      const firstPermittedId = visiblePipelines[0]?.id;
      if (!allowedPipelineIdSet.has(pipelineId) && firstPermittedId) {
        handlePipelineChange(firstPermittedId);
      }
      return;
    }
    if (pipelineId !== "default" && !pipelines.some((p) => p.id === pipelineId)) {
      handlePipelineChange("default");
    }
  }, [pipelinesData, pipelines, pipelineId, allowedPipelineIdSet, visiblePipelines]);

  const allPositions = usePipelinePositions(pipelineId);
  const positions = useMemo(
    () =>
      allPositions
        // The default board hides "inactive" (it has its own tab); non-default
        // boards render it as a column. "discarded" never shows.
        .filter((p) => p.id !== "discarded" && (!isDefaultPipeline || p.id !== "inactive"))
        .sort(
          (a, b) =>
            STAGE_ORDER.indexOf(a.stage as Stage) - STAGE_ORDER.indexOf(b.stage as Stage) ||
            a.order - b.order
        ),
    [allPositions, isDefaultPipeline]
  );
  const visiblePositions = useMemo(
    () => (filtros.etapa ? positions.filter((p) => p.id === filtros.etapa) : positions),
    [positions, filtros.etapa]
  );
  // Ticket columns are per-pipeline and merchant-configurable, and are a
  // different set from the lead positions above.
  const { data: ticketPositionsByStage } = trpc.pipeline.getTicketPositions.useQuery(
    { merchantId: merchantId || "", pipelineId },
    { enabled: !!merchantId && hasTickets(pipelineId), staleTime: 5 * 60 * 1000 }
  );
  // Flattened into one rail in stage order — a pipeline may legitimately lack
  // a whole stage (general boards often have no success positions), so stages
  // with no positions simply contribute nothing.
  const ticketPositions = useMemo<TicketPosition[]>(() => {
    if (!ticketPositionsByStage) return [];
    const byStage = ticketPositionsByStage as Record<string, TicketPosition[]>;
    return STAGE_ORDER.flatMap((stage) =>
      [...(byStage[stage] ?? [])].sort((a, b) => a.order - b.order)
    );
  }, [ticketPositionsByStage]);

  // Ticket categories are configured on the pipeline's agent, not the
  // pipeline — resolve agent by topic, then read its transfer categories.
  const agentTopic = PIPELINE_AGENT_TOPIC[pipelineId] ?? PIPELINE_AGENT_TOPIC["general"];
  const { data: agents } = trpc.agents.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId && hasTickets(pipelineId), staleTime: 5 * 60 * 1000 }
  );
  const targetAgentId = useMemo(
    () => agents?.find((a) => a.topic === agentTopic)?.id,
    [agents, agentTopic]
  );
  const { data: categoriesData } = trpc.agents.getTransferConversationCategories.useQuery(
    { merchantId: merchantId || "", agentId: targetAgentId || "" },
    { enabled: !!merchantId && !!targetAgentId, staleTime: 5 * 60 * 1000 }
  );
  // Raw shape feeds the drawer's category select; the filter chips want
  // {id,label} like every other dropdown in the subheader.
  const categoriasRaw = useMemo(
    () => (categoriesData as Array<{ name: string; description: string }> | undefined) ?? [],
    [categoriesData]
  );
  const categorias = useMemo(
    () => categoriasRaw.map((c) => ({ id: c.name, label: c.name })),
    [categoriasRaw]
  );

  const { data: employees } = trpc.employees.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId, staleTime: 5 * 60 * 1000 }
  );
  // employees.list returns table order; alphabetical is the only order that
  // makes a long asesor list scannable in the filter dropdown.
  const asesores = useMemo(
    () =>
      (employees ?? [])
        .filter((e) => e.status === "active")
        .map((e) => ({ id: e.id, label: e.name }))
        .sort((a, b) => a.label.localeCompare(b.label, "es", { sensitivity: "base" })),
    [employees]
  );
  const employeeNameById = useMemo(
    () => new Map((employees ?? []).map((e) => [e.id, e.name])),
    [employees]
  );

  // Saved asesor presets. Data-scoped employees get none: their dropdown only
  // lists their linked asesores.
  const { data: employeeGroups } = trpc.employeeGroups.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId && !isDataScoped, staleTime: 5 * 60 * 1000 }
  );
  // Only active asesores are selectable, so a group's deactivated members are
  // dropped here — otherwise they would be ids that can never be checked and
  // the group would sit at "some" forever.
  const grupos = useMemo(() => {
    const activeIds = new Set(asesores.map((a) => a.id));
    return (employeeGroups ?? [])
      .map((g) => ({
        id: g.id,
        label: g.name,
        employeeIds: (g.employeeIds ?? []).filter((id) => activeIds.has(id)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "es", { sensitivity: "base" }));
  }, [employeeGroups, asesores]);

  const { data: merchant } = trpc.merchants.get.useQuery(
    { id: merchantId || "" },
    { enabled: !!merchantId, staleTime: 5 * 60 * 1000 }
  );
  const fuentes = useMemo(() => {
    const options = new Map<string, string>(Object.entries(mapSource));
    for (const schema of merchant?.portalSchemas ?? []) {
      if (!options.has(schema.source)) {
        options.set(schema.source, sourceLabel(schema.source) ?? schema.source);
      }
    }
    return Array.from(options, ([id, label]) => ({ id, label }));
  }, [merchant?.portalSchemas]);

  const { data: pautaCampaigns } = trpc.leads.countPautaLeadsByCampaign.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId, staleTime: 5 * 60 * 1000 }
  );
  const { data: campaignRules } = trpc.inboundCampaignRules.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId, staleTime: 5 * 60 * 1000 }
  );
  const pautas = useMemo(() => {
    if (!pautaCampaigns?.length) return [];
    const activeNames = new Set(
      (campaignRules ?? []).filter((rule) => rule.isActive).map((rule) => rule.name)
    );
    const campaigns = pautaCampaigns
      .flatMap((c) =>
        c.campaignName ? [{ name: c.campaignName, isActive: activeNames.has(c.campaignName) }] : []
      )
      .sort(
        (a, b) =>
          Number(b.isActive) - Number(a.isActive) ||
          a.name.localeCompare(b.name, "es", { sensitivity: "base" })
      )
      .map((c) => ({ id: c.name, label: c.isActive ? c.name : `${c.name} (inactiva)` }));
    return [{ id: ANY_PAUTA, label: "Todas las pautas" }, ...campaigns];
  }, [pautaCampaigns, campaignRules]);

  const { data: leadTags } = trpc.leadTags.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId, staleTime: 5 * 60 * 1000 }
  );
  const etiquetas = useMemo(
    () =>
      (leadTags ?? []).map((t) => ({
        id: t.id,
        label: t.status === "archived" ? `${t.name} (archivada)` : t.name,
      })),
    [leadTags]
  );

  const startDate = dateRange?.from ? dayjs(dateRange.from).format("YYYY-MM-DD") : undefined;
  const endDate = dateRange?.to ? dayjs(dateRange.to).format("YYYY-MM-DD") : undefined;

  const queryFilters: TableroQueryFilters = useMemo(
    () => ({
      startDate,
      endDate,
      source: filtros.fuente ?? undefined,
      employeeIds: effectiveAsesores.length ? effectiveAsesores : undefined,
      needsReply: filtros.soloAtencionHumana ? true : undefined,
      ...pautaQueryFilters(filtros.pauta),
      tagIds: filtros.tagIds.length ? filtros.tagIds : undefined,
    }),
    [
      startDate,
      endDate,
      filtros.fuente,
      effectiveAsesores,
      filtros.soloAtencionHumana,
      filtros.pauta,
      filtros.tagIds,
    ]
  );

  // One term, no mode for the user to pick. The backend classifies it: digits
  // key the phone index, anything else fans out to both WhatsApp handle indexes
  // (see helpers/leadSearch.ts). Search overrides the realtime switch entirely —
  // DynamoDB can serve it alongside every filter the switch exists for, so there
  // is no reason to fall back to the stale store for the handle half.
  const searchTerm = debouncedQuery.trim() || undefined;
  const isSearchMode = !!searchTerm && effectiveTab === "activos";

  // Inactivos runs the same search against its own tab (logic/leads.ts
  // searchInactiveLeads), so a phoneless inactive lead is reachable by username
  // there too.
  const inactivoSearch = effectiveTab === "inactivos" ? searchTerm : undefined;

  const searchQuery = trpc.leads.searchActive.useQuery(
    {
      merchantId: merchantId || "",
      search: searchTerm ?? "",
      pipelineId,
      startDate,
      endDate,
      source: filtros.fuente ?? undefined,
      ...pautaQueryFilters(filtros.pauta),
      limit: 200,
    },
    { enabled: !!merchantId && isSearchMode }
  );

  // The name half, fired concurrently rather than after: DynamoDB answers in a
  // few ms and paints immediately, and these rows append when Tinybird returns.
  // A name can only be matched here — it has no index, and begins_with could
  // never find "Juan Martínez" from "martinez".
  const nameQuery = trpc.leads.searchActiveNonRealtime.useQuery(
    {
      ...queryFilters,
      merchantId: merchantId || "",
      pipelineId,
      search: searchTerm,
      page: 0,
      pageSize: 100,
    },
    { enabled: !!merchantId && isSearchMode }
  );

  // Applied to both halves so they narrow identically. Redundant for the
  // Tinybird rows on employee/needsReply (already filtered server-side) and
  // load-bearing for etapa, which no query carries.
  const matchesBoardFilters = useMemo(
    () => (lead: TableroCardLead) => {
      if (effectiveAsesores.length && !effectiveAsesores.includes(lead.employeeId ?? ""))
        return false;
      if (filtros.soloAtencionHumana && lead.lastMessageSource !== "customer") return false;
      if (filtros.etapa && lead.pipelinePositionId !== filtros.etapa) return false;
      if (filtros.tagIds.length && !(lead.tagIds ?? []).some((id) => filtros.tagIds.includes(id)))
        return false;
      return true;
    },
    [effectiveAsesores, filtros.soloAtencionHumana, filtros.etapa, filtros.tagIds]
  );

  const realtimeSearchLeads = useMemo(
    () => ((searchQuery.data?.data ?? []) as TableroCardLead[]).filter(matchesBoardFilters),
    [searchQuery.data, matchesBoardFilters]
  );

  // Name matches minus anything DynamoDB already returned. The realtime copy
  // wins a tie: same lead, fresher row.
  const nonRealtimeSearchLeads = useMemo(() => {
    const seen = new Set(realtimeSearchLeads.map((l) => l.id));
    return ((nameQuery.data?.data ?? []) as TableroCardLead[]).filter(
      (lead) => !seen.has(lead.id) && matchesBoardFilters(lead)
    );
  }, [nameQuery.data, realtimeSearchLeads, matchesBoardFilters]);

  const searchLeads = useMemo(
    () => [...realtimeSearchLeads, ...nonRealtimeSearchLeads],
    [realtimeSearchLeads, nonRealtimeSearchLeads]
  );
  const nonRealtimeIds = useMemo(
    () => new Set(nonRealtimeSearchLeads.map((l) => l.id)),
    [nonRealtimeSearchLeads]
  );

  const positionById = useMemo(
    () => new Map<string, PipelinePosition>(positions.map((p) => [p.id, p])),
    [positions]
  );

  // The default Inactivos and Solicitudes ranges don't count as user filters —
  // otherwise "Limpiar filtros" would always show there, and clearing would
  // strip Solicitudes' month bound (which exists to cap query cost).
  const defaultRange =
    effectiveModo === "solicitudes" ? currentMonthRange() : defaultDateRange(effectiveTab);
  const isDefaultDateRange =
    startDate === (defaultRange?.from ? dayjs(defaultRange.from).format("YYYY-MM-DD") : undefined) &&
    endDate === (defaultRange?.to ? dayjs(defaultRange.to).format("YYYY-MM-DD") : undefined);
  const anyFilter =
    effectiveModo === "solicitudes"
      ? isSolicitudFilterMode(solicitudFiltros) || !!solicitudQuery || !isDefaultDateRange
      : !!(
          filtros.asesores.length ||
          filtros.etapa ||
          filtros.fuente ||
          filtros.pauta ||
          filtros.tagIds.length ||
          filtros.soloAtencionHumana ||
          filtros.query ||
          !isDefaultDateRange
        );
  const handleClearFilters = () => {
    setFiltros(EMPTY_FILTROS);
    setSolicitudFiltros(EMPTY_SOLICITUD_FILTROS);
    setDebouncedQuery("");
    setSolicitudQuery("");
    setDebouncedSolicitudQuery("");
    setDateRange(defaultRange);
    // Clearing filters always lands back in real-time, even after a manual
    // opt-out — the reset-to-default affordance resets the mode too.
    setManualRealtimeOff(false);
  };

  const handleSelectLead = (leadId: string) => {
    posthog.capture("tablero lead opened", {
      merchant_id: merchantId,
      lead_id: leadId,
      tab: effectiveTab,
      pipeline_id: pipelineId,
    });
    setSelectedLeadId(leadId);
  };

  const handleSelectSolicitud = (solicitudId: string) => {
    posthog.capture("tablero solicitud opened", {
      merchant_id: merchantId,
      solicitud_id: solicitudId,
      pipeline_id: pipelineId,
    });
    setSelectedSolicitudId(solicitudId);
  };

  // Navigation, not a context change — filters and date range survive, so
  // coming back to Solicitudes lands on the same board the user left.
  const handleOpenLeadFromSolicitud = (leadId: string) => {
    setSelectedSolicitudId(undefined);
    setModo("leads");
    setSelectedLeadId(leadId);
    posthog.capture("tablero solicitud lead opened", {
      merchant_id: merchantId,
      lead_id: leadId,
      pipeline_id: pipelineId,
    });
  };

  if (!merchantId) return null;

  // Roles unknown must not render an unscoped board. The page is open to every
  // merchant user, so this guard is the only thing between a data-scoped
  // employee and an unscoped board — there is no route-level gate behind it.
  if (isLoadingUser) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }
  if (isDataScoped && employeeIds.length === 0) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-6 text-center text-sm text-neutral-600">
        Tu usuario no tiene un asesor asociado. Contacta a un administrador.
      </div>
    );
  }
  if (isDataScoped && pipelinePermissions.length === 0) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-6 text-center text-sm text-neutral-600">
        No tienes acceso a ningún pipeline. Contacta a un administrador.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-dvh flex-col">
      <SubHeaderFiltros
        pipelines={visiblePipelines}
        pipelineId={pipelineId}
        onPipelineChange={handlePipelineChange}
        vista={vista}
        setVista={setVista}
        tab={effectiveTab}
        setTab={handleTabChange}
        modo={effectiveModo}
        setModo={handleModoChange}
        solicitudFiltros={solicitudFiltros}
        setSolicitudFiltros={setSolicitudFiltros}
        solicitudQuery={solicitudQuery}
        setSolicitudQuery={setSolicitudQuery}
        categorias={categorias}
        filtros={filtros}
        setFiltros={setFiltros}
        dateRange={dateRange}
        setDateRange={setDateRange}
        asesores={isDataScoped ? asesores.filter((a) => employeeIds.includes(a.id)) : asesores}
        grupos={isDataScoped ? [] : grupos}
        showAsesorFilter={showAsesorFilter}
        etapas={positions.map((p) => ({ id: p.id, label: p.displayName }))}
        fuentes={fuentes}
        pautas={pautas}
        etiquetas={etiquetas}
        anyFilter={anyFilter}
        onClear={handleClearFilters}
        realtime={realtime}
        setRealtime={handleRealtimeChange}
        searchActive={isSearchMode}
        realtimeLocked={realtimeLocked}
        isDataScoped={isDataScoped}
      />
      <div className="min-h-0 flex-1">
        {effectiveModo === "solicitudes" ? (
          <SolicitudesBoard
            merchantId={merchantId}
            pipelineId={pipelineId}
            positions={ticketPositions}
            filtros={solicitudFiltros}
            searchQuery={debouncedSolicitudQuery.trim() || undefined}
            startDate={startDate}
            endDate={endDate}
            onSelectSolicitud={handleSelectSolicitud}
            selectedSolicitudId={selectedSolicitudId}
            employeeNameById={employeeNameById}
            anyFilter={anyFilter}
            onClearFilters={handleClearFilters}
          />
        ) : effectiveTab === "inactivos" ? (
          <InactivoSelectionProvider
            // Selection is off during a search: "seleccionar todos" resolves
            // ids from the unfiltered lane, so it would pull in leads the search
            // excluded. Flipping enabled off also clears any pending selection.
            enabled={broadcastEnabled && merchantType !== "development" && !inactivoSearch}
            resetKey={`${startDate ?? ""}|${endDate ?? ""}|${filtros.fuente ?? ""}|${filtros.pauta ?? ""}|${filtros.tagIds.join(",")}`}
          >
            <FunnelComercial
              merchantId={merchantId}
              filters={{
                startDate,
                endDate,
                source: filtros.fuente ?? undefined,
                ...pautaQueryFilters(filtros.pauta),
                tagIds: filtros.tagIds.length ? filtros.tagIds : undefined,
                search: inactivoSearch,
              }}
              onSelectLead={handleSelectLead}
            />
            <InactivoBroadcastBridge />
          </InactivoSelectionProvider>
        ) : isSearchMode ? (
          vista === "kanban" ? (
            <PhoneColumns
              positions={visiblePositions}
              leads={searchLeads}
              nonRealtimeIds={nonRealtimeIds}
              isLoading={searchQuery.isLoading}
              onSelectLead={handleSelectLead}
              selectedLeadId={selectedLeadId}
              employeeNameById={employeeNameById}
            />
          ) : (
            <ListaRows
              rows={searchLeads}
              nonRealtimeIds={nonRealtimeIds}
              positionById={positionById}
              employeeNameById={employeeNameById}
              onSelectLead={handleSelectLead}
              selectedLeadId={selectedLeadId}
              isLoading={searchQuery.isLoading}
            />
          )
        ) : !realtime ? (
          <NonRealtimeSearchBoard
            merchantId={merchantId}
            pipelineId={pipelineId}
            positions={visiblePositions}
            // No `search` here: a term routes to the search branch above, which
            // owns both halves. This board is now filter-only browsing.
            filters={queryFilters}
            vista={vista}
            etapa={filtros.etapa ?? undefined}
            positionById={positionById}
            onSelectLead={handleSelectLead}
            selectedLeadId={selectedLeadId}
            employeeNameById={employeeNameById}
          />
        ) : vista === "kanban" ? (
          <KanbanBoard
            merchantId={merchantId}
            pipelineId={pipelineId}
            positions={visiblePositions}
            filters={queryFilters}
            onSelectLead={handleSelectLead}
            selectedLeadId={selectedLeadId}
            employeeNameById={employeeNameById}
            anyFilter={anyFilter}
            onClearFilters={handleClearFilters}
          />
        ) : (
          <ListaView
            merchantId={merchantId}
            pipelineId={pipelineId}
            positions={visiblePositions}
            filters={queryFilters}
            employeeNameById={employeeNameById}
            onSelectLead={handleSelectLead}
            selectedLeadId={selectedLeadId}
          />
        )}
      </div>
      <ProspectoDrawer
        isOpen={!!selectedLeadId}
        onClose={() => setSelectedLeadId(undefined)}
        leadId={selectedLeadId}
      />
      <TicketDrawer
        isOpen={!!selectedSolicitudId}
        onClose={() => setSelectedSolicitudId(undefined)}
        ticketId={selectedSolicitudId}
        employees={asesores.map((a) => ({ id: a.id, name: a.label }))}
        categories={categoriasRaw}
        ticketPositions={ticketPositions}
        onOpenLead={handleOpenLeadFromSolicitud}
      />
    </div>
  );
};

export default TableroContainer;
